import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Anchor, Box, Group, SimpleGrid, Text } from '@mantine/core';
import { fmtFwdDate } from '@/lib/forwarding';
import { formatMoney, formatOrgName } from '@/lib/format';
import { telHref } from '@/lib/phone';
import type { BargainStepsInfo } from '@/types/deals';

interface ForwardingGoodsHeaderProps {
  readonly info: BargainStepsInfo;
}

function Field({ label, children }: { readonly label: string; readonly children: ReactNode }) {
  return (
    <Box>
      <Text fz="xs" c="gray.7" mb={2}>
        {label}
      </Text>
      <Box fz="sm">{children}</Box>
    </Box>
  );
}

// Шапка шага «Перевозка»: товар, объём к отгрузке, мощность отгрузки, экспедитор (с телефоном),
// дата обновления данных экспедирования.
export default function ForwardingGoodsHeader({ info }: ForwardingGoodsHeaderProps) {
  const { t } = useTranslation();
  const rate = info.loadingConditions?.dailyShippingRate;
  const forwarder = info.forwarder;
  const fwd = info.forwardingInfo;
  const updatedOn = fmtFwdDate(
    fwd?.loadedDeliveryInfo?.updatedOn ?? fwd?.unloadedDeliveryInfo?.updatedOn ?? fwd?.plannedDeliveryInfo?.updatedOn,
  );

  const rateText =
    rate?.value != null
      ? rate.type === 'TS'
        ? `${rate.value} ${t('app.deals.forwarding.rateVehicles')}`
        : `${formatMoney(rate.value)} ${t('app.deals.forwarding.rateTons')}`
      : '—';

  return (
    <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
      <Field label={t('app.deals.summary.goods')}>
        <Text fz="sm" fw={500}>
          {info.goodsType ?? '—'}
        </Text>
      </Field>
      <Field label={t('app.deals.forwarding.goodsVolume')}>
        <Text fz="sm">
          {formatMoney(info.plannedWeight)} {t('app.deals.tons')}
        </Text>
      </Field>
      <Field label={t('app.deals.forwarding.shippingRate')}>
        <Text fz="sm">
          {rateText}
        </Text>
      </Field>
      <Field label={t('app.deals.forwarding.forwarder')}>
        {forwarder?.name ? (
          <Group gap={4} wrap="wrap">
            <Text fz="sm">{formatOrgName(forwarder.name)}</Text>
            {forwarder.phone ? (
              <Anchor href={telHref(forwarder.phone)} fz="sm" c="brand.7">
                {forwarder.phone}
              </Anchor>
            ) : null}
          </Group>
        ) : (
          <Text fz="sm">—</Text>
        )}
      </Field>
      {updatedOn ? (
        <Text fz="xs" c="gray.6" style={{ gridColumn: '1 / -1' }}>
          {t('app.deals.forwarding.updatedOn')} {updatedOn}
        </Text>
      ) : null}
    </SimpleGrid>
  );
}
